import React, { useMemo, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';

export const FORMAT_OPTIONS = [
  { value: '', label: 'Company default — prefix + number (INV-0042)' },
  { value: '{PREFIX}{SEQ}', label: 'PREFIX + number (INV-0042)' },
  { value: '{PREFIX}{YYYY}-{SEQ}', label: 'PREFIX + year + number (INV-2026-0042)' },
  { value: '{PREFIX}{YY}{MM}-{SEQ}', label: 'PREFIX + YYMM + number (INV-2609-0042)' },
  { value: 'custom', label: 'Custom pattern…' },
];

// Live preview of a numbering pattern in the browser (mirrors backend invoice_numbering.format_number).
export const previewNumber = ({ prefix = 'INV-', format, padding = 4, next = 1 }, fallbackFormat = '{PREFIX}{SEQ}') => {
  const d = new Date();
  const fmt = format || fallbackFormat;
  return fmt.replace('{PREFIX}', (prefix ?? 'INV-').trim()).replace('{YYYY}', String(d.getFullYear())).replace('{YY}', String(d.getFullYear() % 100).padStart(2, '0'))
    .replace('{MM}', String(d.getMonth() + 1).padStart(2, '0')).replace('{SEQ}', String(Math.max(1, Number(next) || 1)).padStart(Math.min(8, Math.max(1, Number(padding) || 4)), '0'));
};

// Numbering editor shared by Settings → Invoice template (company sequence) and Document Style templates.
export const NumberingFields = ({ value, onChange, testPrefix = 'numbering', fallbackFormat = '{PREFIX}{SEQ}', hint }) => {
  const v = value || {};
  const [customMode, setCustomMode] = useState(() => !FORMAT_OPTIONS.some((o) => o.value === (v.format || '')));
  const known = FORMAT_OPTIONS.some((o) => o.value === (v.format || ''));
  const selectVal = customMode || !known ? 'custom' : (v.format || '');
  const set = (patch) => onChange({ ...v, ...patch });
  const preview = useMemo(() => previewNumber({ prefix: v.prefix, format: v.format, padding: v.padding, next: v.next_number }, fallbackFormat), [v, fallbackFormat]);
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-block`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <Label className="text-[11px] text-slate-400">Prefix</Label>
          <Input value={v.prefix ?? ''} maxLength={20} onChange={(e) => set({ prefix: e.target.value })} placeholder="INV-" className="h-8 text-sm" data-testid={`${testPrefix}-prefix`} />
        </div>
        <div>
          <Label className="text-[11px] text-slate-400">Pattern</Label>
          <select value={selectVal} onChange={(e) => { const c = e.target.value === 'custom'; setCustomMode(c); set({ format: c ? (v.format || '{PREFIX}{SEQ}') : e.target.value }); }}
            className="h-8 w-full rounded-md border border-slate-700 bg-transparent text-sm px-2" data-testid={`${testPrefix}-format`}>
            {FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px] text-slate-400">Digits</Label>
          <Input type="number" min={1} max={8} value={v.padding ?? 4} onChange={(e) => set({ padding: e.target.value })} className="h-8 text-sm" data-testid={`${testPrefix}-padding`} />
        </div>
        <div>
          <Label className="text-[11px] text-slate-400">Next number</Label>
          <Input type="number" min={1} value={v.next_number ?? ''} onChange={(e) => set({ next_number: e.target.value })} placeholder="e.g. 10500" className="h-8 text-sm" data-testid={`${testPrefix}-next`} />
        </div>
      </div>
      {selectVal === 'custom' && (
        <Input value={v.format || ''} maxLength={40} onChange={(e) => set({ format: e.target.value })} placeholder="{PREFIX}{YYYY}-{SEQ}  (tokens: {PREFIX} {YYYY} {YY} {MM} {SEQ})" className="h-8 text-xs font-mono" data-testid={`${testPrefix}-custom-format`} />
      )}
      <p className="text-[11px] text-slate-400" data-testid={`${testPrefix}-preview`}>
        Next invoice will be <span className="font-mono text-white">{preview}</span>{hint ? ` — ${hint}` : ''}
      </p>
      {!v.format && (
        <p className="text-[11px] text-slate-500" data-testid={`${testPrefix}-unified-note`}>
          One format for every invoice — job invoices and Daily Site Invoices share this sequence and look.
        </p>
      )}
    </div>
  );
};

// Defaults carried by an invoice template.
export const TemplateDefaultsFields = ({ value, onChange, testPrefix = 'tpl' }) => {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`${testPrefix}-defaults`}>
      <div><Label className="text-[11px] text-slate-400">Terms (days)</Label><Input type="number" min={0} max={365} value={v.terms_days ?? ''} onChange={(e) => set({ terms_days: e.target.value })} placeholder="30" className="h-8 text-sm" data-testid={`${testPrefix}-terms`} /></div>
      <div><Label className="text-[11px] text-slate-400">Tax label</Label><Input value={v.tax_label ?? ''} maxLength={30} onChange={(e) => set({ tax_label: e.target.value })} placeholder="GST" className="h-8 text-sm" data-testid={`${testPrefix}-tax-label`} /></div>
      <div><Label className="text-[11px] text-slate-400">Tax %</Label><Input type="number" min={0} max={30} step="0.01" value={v.tax_rate_pct ?? ''} onChange={(e) => set({ tax_rate_pct: e.target.value })} placeholder="5" className="h-8 text-sm" data-testid={`${testPrefix}-tax-rate`} /></div>
      <div><Label className="text-[11px] text-slate-400">Default notes</Label><Input value={v.notes ?? ''} maxLength={500} onChange={(e) => set({ notes: e.target.value })} placeholder="Printed under totals" className="h-8 text-sm" data-testid={`${testPrefix}-notes`} /></div>
    </div>
  );
};
